import * as React from "react";
import CloseIcon from '@mui/icons-material/Close';
import { ButtonBase, Accordion, AccordionSummary, AccordionDetails, Typography } from "@mui/material";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { getSite } from "../ducks/selectors";
import { useSelector } from "react-redux";
import { getUserAccess } from 'modules/cbre-flow-api-ducks/selectors';
import { getQuickLinkContactsList } from "modules/cbre-flow-view-management";


type Contact = {
  siteContactName: string;
  siteContactEmail: string;
  description: string;
};

type ContactSectionProps = {
  contacts: Contact[];
};

const ContactSection: React.FC<ContactSectionProps> = ({ contacts }) => (
  <section className="contact-section pl-2">
    {
      contacts.length === 0 ? (
        <p className="text-center p-3 m-0">No contact information available for this site.</p>
      ) : (
    contacts.map((contact, index) => (
      <article key={index} className="contact-card" >
        <h3>{contact.siteContactName}</h3>
        <a href={`mailto:${contact.siteContactEmail}`} className="contact-email">{contact.siteContactEmail}</a>
        <p>{contact.description}</p>
      </article>
    ))
    )}
  </section>
);

const contactsData = [
  {
    title: "Contact",
    howIcanHelp: "How I can help",
    contacts: [
      {
        name: "CBRE Site Team",
        email: "eLIS.Springhouse@cbre.com",
        description: "General inquiries about the ELIS and CBRE services"
      },
      {
        name: "Agilent Site Team",
        email: "jnj-lms.shm@agilent.com",
        description: "General inquiries about your Agilent Service"
      },
      {
        name: "Jan Suessenguth - Instrument Lifecycle Coordinator",
        email: "jan.suessenguth@cbre.com",
        description: "Onboarding your new instruments into the lab and decommissioning your old instrumentation"
      },
      {
        name: "Uilton Dossantos - Site Lead",
        email: "uilton.dossantos@cbre.com",
        description: "I ensure ELIS provides the best services for your lab"
      }
    ]
  }
];

const HelpInfoPopup: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const allQuickLinks:any[] = useSelector(getQuickLinkContactsList);
  const { clients = [] } = useSelector(getUserAccess) || {};
  const [expandedAccordion, setExpandedAccordion] = React.useState<string | false>('site-0');

  // Group sites by their contact information (assuming each site has unique contacts)
  // For now, using a simple structure. Adjust based on your actual data structure
  const hasSingleSite = allQuickLinks.length === 1;

  const handleAccordionChange = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpandedAccordion(isExpanded ? panel : false);
  };

  const hasLinks = (allQuickLinks.length > 0 || (hasSingleSite && allQuickLinks[0].sitecontacts?.length > 0));
  return (
    <>
      <section className="help-section">
        {hasLinks && (
          <div className="help-header mb-2">
            <h1>Need help?</h1>
            <ButtonBase onClick={onClose} focusRipple>
              <CloseIcon />
            </ButtonBase>
          </div>
        )}
        {!hasLinks ? (
          <div className="help-content">
            <h3 className="text-center">No help available at this time.</h3>
          </div>
        ) : hasSingleSite ? (
          // Single site view - simple list
          <>
            <div className="contact-header" style={{ paddingLeft: '16px' }}>
              <h2 style={{ flex: 2 }}>{"Contact"}</h2>
              <h2 style={{ flex: 1 }}>{"How I can help"}</h2>
            </div>
            <ContactSection contacts={allQuickLinks[0]?.sitecontacts} />
          </>
        ) : (
          // Multiple sites view - accordion
          <div style={{ padding: '0 16px' }}>
            <div className="contact-header" style={{ paddingLeft: '16px' }}>
              <h2 style={{ flex: 2 }}>{"Contact"}</h2>
              <h2 style={{ flex: 1 }}>{"How I can help"}</h2>
            </div>
            {allQuickLinks.map((site, index) => (
              <Accordion
                key={`site-${index}`}
                expanded={expandedAccordion === `site-${index}`}
                onChange={handleAccordionChange(`site-${index}`)}
                sx={{
                  boxShadow: 'none',
                  '&:before': { display: 'none' },
                  border: 'none',
                  marginBottom: 0
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{
                    backgroundColor: 'rgba(0, 63, 45, 0.08) !important',
                    minHeight: '48px',
                    '&.Mui-expanded': {
                      minHeight: '48px'
                    },
                    '& .MuiAccordionSummary-content': {
                      margin: '12px 0'
                    }
                  }}
                >
                  <Typography style={{ fontWeight: 500 }}>{site.siteName}</Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ padding: '16px 0' }}>
                  <ContactSection contacts={site.sitecontacts} />
                </AccordionDetails>
              </Accordion>
            ))}
          </div>
        )}
      </section>
    </>
  );
};

export default HelpInfoPopup;